import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Upload, Loader2, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, CATEGORY_LABELS } from "./types";

interface MediaUploadZoneProps {
  onUpload: (files: FileList, category: string, tags: string[]) => void;
  uploading: boolean;
}

export default function MediaUploadZone({ onUpload, uploading }: MediaUploadZoneProps) {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileList | null>(null);
  const [category, setCategory] = useState("uncategorized");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) setPendingFiles(e.dataTransfer.files);
  }, []);

  const handleFileSelect = (files: FileList | null) => {
    if (files?.length) setPendingFiles(files);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput("");
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const removeTag = (tag: string) => setTags(tags.filter((t) => t !== tag));

  const handleSubmit = () => {
    if (!pendingFiles) return;
    onUpload(pendingFiles, category, tags);
    setPendingFiles(null);
    setCategory("uncategorized");
    setTags([]);
    setTagInput("");
  };

  return (
    <div className="space-y-4 rounded-xl border border-border p-4 bg-card">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
          dragOver ? "border-primary bg-primary/5 scale-[1.01]" : "border-border hover:border-primary/50 hover:bg-accent/30"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*,audio/*,.pdf,.svg"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files)}
        />
        {uploading ? (
          <Loader2 className="h-7 w-7 mx-auto mb-2 animate-spin text-primary" />
        ) : (
          <Upload className={`h-7 w-7 mx-auto mb-2 transition-colors ${dragOver ? "text-primary" : "text-muted-foreground"}`} />
        )}
        <p className="text-sm font-medium text-foreground">
          {uploading ? "Uploading..." : pendingFiles ? `${pendingFiles.length} file(s) ready` : "Drag files here or click to browse"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG, MP4, PDF — max 20 MB</p>
      </div>

      {/* Metadata fields - show when files are pending */}
      {pendingFiles && !uploading && (
        <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-2 gap-3">
            {/* Category */}
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-xs">
                      {CATEGORY_LABELS[cat] || cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div className="space-y-1.5">
              <Label className="text-xs">Tags</Label>
              <div className="flex gap-1.5">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add a tag..."
                  className="h-9 text-xs flex-1"
                />
                <IconButton variant="outline" className="h-9 w-9" onClick={addTag} type="button" label={t("actions.addTag")}>
                  <Plus className="h-3.5 w-3.5" />
                </IconButton>
              </div>
            </div>
          </div>

          {/* Tag chips */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-[10px] gap-1 pr-1">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-destructive transition-colors">
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <Button size="sm" className="w-full" onClick={handleSubmit} disabled={uploading}>
            <Upload className="h-4 w-4 mr-1" />
            Upload {pendingFiles.length} file{pendingFiles.length > 1 ? "s" : ""}
          </Button>
        </div>
      )}
    </div>
  );
}
